import {
  MeetingAccessAction,
  MeetingAccessVia,
  MeetingShareCandidateDto,
  MeetingSharePersonDto,
} from '@interfaces/meetings/responses/meeting-sharing.response';
import { Option } from '@interfaces/common/option';

/** Prazos do link (card 3241): 7 dias é o padrão; "sem prazo" também vale. */
export type PrazoDoLink = '1' | '7' | '30' | 'sem-prazo';

/** Opções do select de prazo, na ordem da tela. */
export const PRAZOS_DO_LINK: Option[] = [
  { label: '1 dia', value: '1' },
  { label: '7 dias', value: '7' },
  { label: '30 dias', value: '30' },
  { label: 'Sem prazo', value: 'sem-prazo' },
];

/** O prazo escolhido no formato do pedido (`expiresInDays`, `noExpiration`). */
export function pedidoDoPrazo(prazo: PrazoDoLink): {
  expiresInDays: number | null;
  noExpiration: boolean;
} {
  return prazo === 'sem-prazo'
    ? { expiresInDays: null, noExpiration: true }
    : { expiresInDays: Number(prazo), noExpiration: false };
}

/** URL que a pessoa abre: a tela troca o token pelo acesso e segue para o detalhe. */
export function urlDoLink(origem: string, token: string): string {
  return `${origem.replace(/\/+$/, '')}/dashboard/meetings/compartilhada/${encodeURIComponent(token)}`;
}

/**
 * Candidatos que ainda não têm acesso direto, como opções do select. Quem entrou pelo link continua na lista:
 * compartilhar com ele converte o acesso em direto, sem prazo, e revogar o link deixa de tirá-lo. Quem participou
 * da reunião aparece com "Participou · já tem acesso" e não pode ser marcado (card 3333, task 3337).
 */
export function opcoesDeCandidatos(
  candidatos: MeetingShareCandidateDto[],
  pessoas: MeetingSharePersonDto[]
): Option[] {
  const jaTem = new Set(pessoas.filter(p => !p.viaLink).map(p => p.userId));
  return candidatos
    .filter(c => !jaTem.has(c.userId))
    .map(c => {
      const displayName = c.email ? `${c.name} · ${c.email}` : c.name;
      return c.alreadyHasAccess
        ? { label: `${displayName} — Participou · já tem acesso`, value: c.userId, isDisable: true }
        : { label: displayName, value: c.userId };
    });
}

const ROTULO_DA_VIA: Record<MeetingAccessVia, string> = {
  Participated: 'participou',
  Shared: 'compartilhada',
  Link: 'pelo link',
  GeneralAccess: 'acesso geral',
  BusinessLink: 'vínculo de negócio',
  ExternalParticipant: 'participante de fora',
};

const ROTULO_DA_ACAO: Record<MeetingAccessAction, string> = {
  OpenedDetail: 'Abriu a meeting',
  WatchedRecording: 'Assistiu à gravação',
  DownloadedRecording: 'Baixou o vídeo',
  ReadTranscript: 'Leu a transcrição',
};

/** "pelo link", "compartilhada"... */
export function rotuloDaVia(via: MeetingAccessVia): string {
  return ROTULO_DA_VIA[via] ?? via;
}

/** "Assistiu à gravação", "Leu a transcrição"... */
export function rotuloDaAcao(acao: MeetingAccessAction): string {
  return ROTULO_DA_ACAO[acao] ?? acao;
}

/** Ícone PrimeIcons da ação. */
export function iconeDaAcao(acao: MeetingAccessAction): string {
  switch (acao) {
    case 'WatchedRecording':
      return 'pi-play';
    case 'DownloadedRecording':
      return 'pi-download';
    case 'ReadTranscript':
      return 'pi-align-left';
    default:
      return 'pi-eye';
  }
}

/** Acessos que a lista mostra antes de "Ver todos". */
export const ACESSOS_VISIVEIS = 5;
